import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { SavedAnalysesListSchema } from 'libs/end-to-end/src/saved-analysis/SavedAnalysisDefinitions';
import { SavedAnalysisOutputSchema } from 'libs/end-to-end/src/saved-analysis/SavedAnalysisDefinitions';
import { Result } from 'libs/end-to-end/src/result';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';

interface CreateAnalysisArgs {
  customVizRequest: CustomVizRequest | undefined;
}

interface SavedAnalysisContext {
  savedAnalysesList: SavedAnalysisOutputSchema[] | null;
  fetchAnalysesList: () => Promise<Result<
    SavedAnalysesListSchema,
    BaseError
  > | null> | void;
  createAnalysis: (
    args: CreateAnalysisArgs,
  ) => Promise<Result<SavedAnalysisOutputSchema, BaseError> | null> | void;
  getSavedAnalysisByCustomVizRequest: (
    args: CreateAnalysisArgs,
  ) => Promise<Result<SavedAnalysisOutputSchema, BaseError> | null> | void;
  deleteAnalysis: (args: {
    savedAnalysisId: string;
  }) => Promise<Result<EmptyObject, BaseError> | null> | void;
}

const SavedAnalysisContext = createContext<SavedAnalysisContext>({
  savedAnalysesList: null,
  fetchAnalysesList: () => {},
  createAnalysis: () => {},
  deleteAnalysis: () => {},
  getSavedAnalysisByCustomVizRequest: () => {},
});

export const useSavedAnalysis = () => {
  return useContext(SavedAnalysisContext);
};

export const SavedAnalysisRenderer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const { userAuthenticatedApiCall } = useUser();

  const [savedAnalysesList, setSavedAnalysesList] = useState<
    SavedAnalysisOutputSchema[] | null
  >(null);

  const fetchAnalysesList = useCallback(async () => {
    if (
      !selectedOrganizationId ||
      !selectedDatabaseId ||
      !selectedDatabaseRevisionId
    ) {
      return null;
    }

    setSavedAnalysesList(null);

    const response = await userAuthenticatedApiCall({
      url: `${getApiBaseUrl()}/saved-analysis?organizationId=${selectedOrganizationId}&databaseId=${selectedDatabaseId}&databaseRevisionId=${selectedDatabaseRevisionId}`,
      responseSchema: SavedAnalysesListSchema,
      requestOptions: {
        method: 'GET',
      },
    });

    if (response && response.resultType === 'ok') {
      setSavedAnalysesList(response.value.savedAnalysesList);
    }

    return response;
  }, [
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    userAuthenticatedApiCall,
  ]);

  const getSavedAnalysisByCustomVizRequest = useCallback(
    async ({ customVizRequest }: CreateAnalysisArgs) => {
      if (!customVizRequest) {
        return null;
      }

      return await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/saved-analysis/custom-viz-request?organizationId=${selectedOrganizationId}&databaseId=${selectedDatabaseId}&databaseRevisionId=${selectedDatabaseRevisionId}`,
        responseSchema: SavedAnalysisOutputSchema,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(customVizRequest),
        },
      });
    },
    [
      selectedDatabaseId,
      selectedDatabaseRevisionId,
      selectedOrganizationId,
      userAuthenticatedApiCall,
    ],
  );

  const createAnalysis = useCallback(
    async ({ customVizRequest }: CreateAnalysisArgs) => {
      if (!customVizRequest) {
        return null;
      }

      const response = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/saved-analysis?organizationId=${selectedOrganizationId}&databaseId=${selectedDatabaseId}&databaseRevisionId=${selectedDatabaseRevisionId}`,
        responseSchema: SavedAnalysisOutputSchema,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ customVizRequest }),
        },
      });

      if (response && response.resultType === 'ok') {
        setSavedAnalysesList((prev) => [response.value, ...(prev ?? [])]);
      }

      return response;
    },
    [
      selectedDatabaseId,
      selectedDatabaseRevisionId,
      selectedOrganizationId,
      userAuthenticatedApiCall,
    ],
  );

  const deleteAnalysis = useCallback(
    async ({ savedAnalysisId }: { savedAnalysisId: string }) => {
      const response = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/saved-analysis?organizationId=${selectedOrganizationId}&databaseId=${selectedDatabaseId}&id=${savedAnalysisId}`,
        responseSchema: EmptyObject,
        requestOptions: {
          method: 'DELETE',
        },
      });

      // if delete request succeeds we make an optimistic update and remove the analysis from the list
      if (response && response.resultType === 'ok') {
        setSavedAnalysesList(
          (prev) =>
            prev?.filter((sA) => sA.savedAnalysisId !== savedAnalysisId) ??
            null,
        );
      }

      return response;
    },
    [userAuthenticatedApiCall, selectedOrganizationId, selectedDatabaseId],
  );

  const value = useMemo(
    () => ({
      savedAnalysesList,
      fetchAnalysesList,
      createAnalysis,
      deleteAnalysis,
      getSavedAnalysisByCustomVizRequest,
    }),
    [
      savedAnalysesList,
      fetchAnalysesList,
      createAnalysis,
      deleteAnalysis,
      getSavedAnalysisByCustomVizRequest,
    ],
  );

  useAsyncEffect(
    async (isAlive) => {
      if (!isAlive() || savedAnalysesList === null) {
        await fetchAnalysesList();
      }
    },
    [fetchAnalysesList, savedAnalysesList],
  );

  return (
    <SavedAnalysisContext.Provider value={value}>
      {children}
    </SavedAnalysisContext.Provider>
  );
};
